import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Stack, styled, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/FormProvider';
import { useStores } from 'src/models';
import * as Yup from 'yup';

type FormValuesProps = {
  id: string;
  password: string;
};
const defaultValues = {
  id: '',
  password: '',
};

const LoginSchema = Yup.object().shape({
  id: Yup.string().required('Email is required'),
  password: Yup.string().required('Password is required'),
});

export default function Login() {
  const { authStore } = useStores();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const { t } = useTranslation();

  const onSubmit = async (data: FormValuesProps) => {
    setIsLoading(true);
    try {
      const result = await authStore.login(data.id, data.password, false);
      if (result && !result.success) {
        enqueueSnackbar(result?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
      methods.reset();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoginContainer>
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <LoginBox>
          <LogoBox>
            <img
              src={'/assets/images/innerwave_logo.png'}
              alt="logo"
              style={{ maxWidth: '150px' }}
            />
          </LogoBox>
          <Box>
            <LoginTitle>{t('login.title')}</LoginTitle>
          </Box>
          <Box>
            <LoginSubTitle>{t('login.subtitle1')}</LoginSubTitle>
            <LoginSubTitle>{t('login.subtitle2')}</LoginSubTitle>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Box>
              <FormLabel variant="caption">{t('login.email')}</FormLabel>
              <RHFTextField name="id" placeholder={t('login.emailPlaceholder')} fullWidth />
            </Box>
            <Box>
              <FormLabel variant="caption">{t('login.password')}</FormLabel>
              <RHFTextField
                name="password"
                type="password"
                placeholder={t('login.passwordPlaceholder')}
                fullWidth
              />
            </Box>
          </Box>
          <LoginButton type="submit" loading={isLoading}>
            {t('login.button')}
          </LoginButton>
        </LoginBox>
      </FormProvider>
    </LoginContainer>
  );
}

const LoginContainer = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  padding: theme.spacing(4),
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.navBackgrounColor,
}));

const LoginBox = styled(Stack)(({ theme }) => ({
  minWidth: '480px',
  minHeight: '700px',
  display: 'flex',
  flexDirection: 'column',
  gap: '30px',
  padding: '60px',
  backgroundColor: theme.palette.common.white,
  borderRadius: '5px',
}));
const LogoBox = styled(Stack)(({ theme }) => ({
  marginTop: '50px',
}));
const LoginTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[900],
  fontFamily: 'Open Sans',
  fontWeight: '700',
  fontSize: '45px',
}));
const LoginSubTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[700],
  lineHeight: theme.spacing(3),
  fontSize: '16px',
}));
const FormLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[500],
  lineHeight: theme.spacing(2),
  padding: '15px 10px',
}));
const LoginButton = styled(LoadingButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  height: '35px',
  boxShadow: '1px 1px 2px 0px rgba(0, 0, 0, 0.35)',
  textAlign: 'center',
  marginTop: '30px',
  marginBottom: '60px',
  width: '100%',
  padding: '23px',
  fontSize: '16px',
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
  },
}));
